import type { ReactNode } from 'react';
import { css } from '@emotion/react';
import { Modal, ModalControlModalProps } from '@frontend/design-system';

interface ActionModalProps {
  modalProps: ModalControlModalProps;
  isSaving?: boolean;
  minWidth?: number;
  children: ReactNode;
}

/**
 * Modal shell that blocks closing while saving. The design-system Modal unmounts its children when hidden,
 * so stateful content rendered inside resets automatically every time the modal is reopened.
 */
export const ActionModal = ({ modalProps, isSaving, minWidth = 520, children }: ActionModalProps) => (
  <Modal
    {...modalProps}
    onClose={() => !isSaving && modalProps.onClose()}
    minWidth={minWidth}
    disableCloseOnEscape
    disableCloseOnOverlayClick
  >
    {children}
  </Modal>
);

interface ActionModalContentProps {
  title: ReactNode;
  onClose: () => void;
  isSaving?: boolean;
  confirmLabel: string;
  savingLabel: string;
  confirmDisabled?: boolean;
  destructive?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}

export const ActionModalContent = ({
  title,
  onClose,
  isSaving,
  confirmLabel,
  savingLabel,
  confirmDisabled,
  destructive,
  onConfirm,
  children,
}: ActionModalContentProps) => {
  const handleClose = () => {
    if (!isSaving) onClose();
  };

  const handleConfirm = () => {
    if (!confirmDisabled && !isSaving) onConfirm();
  };

  return (
    <>
      <Modal.Header onClose={handleClose}>{title}</Modal.Header>
      <Modal.Body css={bodyStyle}>{children}</Modal.Body>
      <Modal.Footer
        primary={{
          label: isSaving ? savingLabel : confirmLabel,
          onClick: handleConfirm,
          disabled: confirmDisabled || isSaving,
          destructive,
        }}
        secondary={{ label: 'Cancel', onClick: handleClose, disabled: isSaving }}
      />
    </>
  );
};

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
  padding-top: var(--fabric-space-padding-md);
`;
